import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import { AuthService } from '../../AuthService';
import { usePremiumUpgrade } from '../../hooks/usePremiumUpgrade';
import Sheet from '../ui/Sheet';
import SocialAuthButtons from '../SocialAuth/SocialAuthButtons';
import CheckoutErrorNotice from '../CheckoutErrorNotice/CheckoutErrorNotice';
import { PRICING_FEATURE_ROWS } from '../../constants/pricingFeatures';
import {
  PREMIUM_DECISION_PRICE_LINE,
  PREMIUM_AUTH_UNLOCK_CTA_LABEL,
} from '../../constants/pricing';
import { captureGaIdentity } from '../../utils/analytics';
import { storePendingComparisonAdd } from '../../comparisonAccess';
import './ComparisonAccessSheet.css';

const SHEET_ROWS = ['compare', 'decision', 'historical_trends', 'qa'];

function AccessRow({ row }) {
  return (
    <li>
      <span>{row.label}</span>
      <span className="access-sheet-tiers">
        <em>Basis {row.basis}</em>
        <strong>Premium {row.premium}</strong>
      </span>
    </li>
  );
}

export default function ComparisonAccessSheet({
  sheet,
  onClose,
  onAuthenticated,
}) {
  const { loginWithGoogle, loginWithApple, refreshUser } = useAuth();
  const location = useLocation();
  const nextPath = `${location.pathname}${location.search}`;
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const reason = sheet?.reason;
  const isAuth = reason === 'auth';

  const { startCheckout, checkoutError, isStartingCheckout } = usePremiumUpgrade({
    returnPath: typeof window !== 'undefined' ? window.location.pathname : '/institution-list',
    source: 'comparison_access_sheet',
    placement: 'comparison_access_sheet',
    feature: 'decision',
  });

  const finish = async (result) => {
    if (!result?.success) {
      setError(result?.error || 'Kunne ikke oprette kontoen.');
      return;
    }
    const currentUser = await refreshUser();
    const user = currentUser || result.user;
    onAuthenticated?.(user);
  };

  const handleEmail = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    const gaIdentity = await captureGaIdentity();
    const result = await AuthService.register(
      '',
      email,
      '',
      '',
      password,
      password,
      gaIdentity.client_id,
      gaIdentity.session_id
    );
    setSubmitting(false);
    await finish(result);
  };

  const rows = PRICING_FEATURE_ROWS.filter((row) => SHEET_ROWS.includes(row.id));

  return (
    <Sheet open={Boolean(sheet)} onClose={onClose}>
      <div className="access-sheet">
        <div className="access-sheet-header">
          <h2>
            {isAuth
              ? 'Opret en gratis konto for at sammenligne'
              : 'Den forskel kræver Premium'}
          </h2>
          <button type="button" className="access-sheet-close" onClick={onClose} aria-label="Luk">
            ×
          </button>
        </div>
        <p className="access-sheet-lead">
          {isAuth
            ? 'En Basis-konto lader dig sammenligne to institutioner på de seneste scorer. Observationer, historik og spørgsmål til rapporten er Premium.'
            : 'Du kan sammenligne to institutioner på scorer. Premium viser observationerne, om kvaliteten har holdt, og hvilken institution der står stærkest.'}
        </p>
        <p className="access-sheet-price">{PREMIUM_DECISION_PRICE_LINE}</p>
        <ul className="access-sheet-list">
          {rows.map((row) => (
            <AccessRow key={row.id} row={row} />
          ))}
        </ul>

        {isAuth ? (
          <>
            <SocialAuthButtons
              mode="register"
              dividerPosition="after"
              loginWithGoogle={loginWithGoogle}
              loginWithApple={loginWithApple}
              onSignedIn={(result) => finish(result)}
              onError={(message) => setError(message)}
            />
            <form className="access-sheet-form" onSubmit={handleEmail}>
              <label>
                E-mail
                <input
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  required
                />
              </label>
              <label>
                Adgangskode
                <input
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  minLength={8}
                  required
                />
              </label>
              {error && <p className="access-sheet-error">{error}</p>}
              <button type="submit" className="access-sheet-primary" disabled={submitting}>
                {submitting ? 'Opretter konto...' : 'Opret Basis-konto'}
              </button>
            </form>
            <p className="access-sheet-login">
              Har du allerede en konto?{' '}
              <Link
                to={`/login?next=${encodeURIComponent(nextPath)}`}
                onClick={() => {
                  if (sheet?.institution) storePendingComparisonAdd(sheet.institution);
                }}
              >
                Log ind
              </Link>
            </p>
          </>
        ) : (
          <>
            <CheckoutErrorNotice message={checkoutError} onRetry={startCheckout} />
            <button
              type="button"
              className="access-sheet-primary"
              onClick={startCheckout}
              disabled={isStartingCheckout}
            >
              {isStartingCheckout ? 'Åbner betaling...' : PREMIUM_AUTH_UNLOCK_CTA_LABEL}
            </button>
            <p className="access-sheet-note">Én betaling. Adgangen udløber efter et år og fornyes ikke.</p>
          </>
        )}
      </div>
    </Sheet>
  );
}
